/**
 * zPlanem Design Tokens — Color Palette
 *
 * Inspired by home construction & apartment renovation:
 *   gray  → concrete, foundations, raw structure
 *   white → walls, clean surfaces, plaster
 *   steel → metallic structural elements, fixtures
 */

// ─── Material Palettes ───────────────────────────────────────────────

export const gray = {
	50: "#fafafa",
	100: "#f0f0f0",
	200: "#d4d4d4",
	300: "#b0b0b0",
	400: "#8c8c8c",
	500: "#6b6b6b",
	600: "#4a4a4a",
	700: "#3a3a3a",
	800: "#2d2d2d",
	900: "#1a1a1a",
	950: "#0f0f0f",
} as const

export const white = {
	pure: "#ffffff",
	50: "#fafafa",
	100: "#f5f5f5",
	200: "#eeeeee",
	300: "#e0e0e0",
	400: "#d6d6d6",
} as const

export const steel = {
	50: "#eceff1",
	100: "#cfd8dc",
	200: "#b0bec5",
	300: "#90a4ae",
	400: "#78909c",
	500: "#607d8b",
	600: "#546e7a",
	700: "#455a64",
	800: "#37474f",
	900: "#263238",
} as const

export const steelBlue = {
	background: "#172A34",
	surface: "#223944",
	raised: "#2A4450",
	muted: "#1E343F",
} as const

// Greige surface system from the Figma portal redesign (ZPL-343).
// Values are pixel-sampled from rendered frames (±1–2/channel) until the
// Figma variables become extractable (ZPL-347 owns reconciliation).
export const porcelain = {
	background: "#ECEAE4",
	surface: "#FAF9F6",
	muted: "#D9D5CB",
	border: "#E5E3DB",
} as const

export const brand = {
	iconIvory: "#F7F0E4",
	// Warm text hierarchy from the Figma portal redesign (ZPL-346):
	// headings/body #1B1A17, secondary body #56534C, muted labels/meta —
	// design shows #8C887E, darkened to #6C6961 (nearest same-hue value
	// holding WCAG AA 4.5:1 on the #ECEAE4 page background).
	inkText: "#1B1A17",
	secondaryText: "#56534C",
	mutedText: "#6C6961",
	// Navy interactive family from the Figma portal redesign (ZPL-344).
	// Pixel-sampled (±1–2/channel) pending Figma variable extraction
	// (ZPL-347/ZPL-342). navy = primary buttons, FAB, logo tile, focus;
	// navyEmphasis = emphasis links; navyActive = active-nav indicator.
	navy: "#22384C",
	navyEmphasis: "#304456",
	navyActive: "#3D5062",
	navyFill: "rgba(34, 56, 76, 0.12)",
} as const

// ─── Semantic Tokens ─────────────────────────────────────────────────

export const semantic = {
	/** Main background */
	background: porcelain.background,
	/** Dark-mode background */
	backgroundDark: steelBlue.background,
	/** Elevated surface cards */
	surface: porcelain.surface,
	surfaceDark: steelBlue.surface,

	/** Primary body text */
	textPrimary: brand.inkText,
	textPrimaryDark: brand.iconIvory,
	/** Secondary / muted text */
	textSecondary: brand.mutedText,
	textSecondaryDark: "rgba(247, 240, 228, 0.66)",

	/** Navy interactive accent for readable text, borders, focus, and chat bubbles */
	accent: brand.navy,
	accentHover: brand.navyEmphasis,
	accentReadable: brand.navy,
	/** Soft accent for translucent fills and non-text highlights */
	accentLight: brand.navyFill,

	/** Borders & dividers — solid warm hairline in light mode (ZPL-343) */
	border: porcelain.border,
	borderDark: "rgba(247, 240, 228, 0.12)",

	/** Ambient glow */
	glow: brand.navyFill,
	glowWarm: brand.navyFill,
} as const

// ─── CSS Custom Properties Helper ────────────────────────────────────

/**
 * Returns a flat record of CSS custom property key-value pairs
 * that can be applied to a root element's style.
 *
 * Usage (web only):
 *   Object.entries(cssVariables).forEach(([k, v]) =>
 *     document.documentElement.style.setProperty(k, v)
 *   );
 */
export const cssVariables: Record<string, string> = {
	"--color-gray-50": gray[50],
	"--color-gray-100": gray[100],
	"--color-gray-200": gray[200],
	"--color-gray-300": gray[300],
	"--color-gray-400": gray[400],
	"--color-gray-500": gray[500],
	"--color-gray-600": gray[600],
	"--color-gray-700": gray[700],
	"--color-gray-800": gray[800],
	"--color-gray-900": gray[900],
	"--color-gray-950": gray[950],

	"--color-white-pure": white.pure,
	"--color-white-50": white[50],
	"--color-white-100": white[100],
	"--color-white-200": white[200],
	"--color-white-300": white[300],
	"--color-white-400": white[400],

	"--color-steel-50": steel[50],
	"--color-steel-100": steel[100],
	"--color-steel-200": steel[200],
	"--color-steel-300": steel[300],
	"--color-steel-400": steel[400],
	"--color-steel-500": steel[500],
	"--color-steel-600": steel[600],
	"--color-steel-700": steel[700],
	"--color-steel-800": steel[800],
	"--color-steel-900": steel[900],

	"--color-steel-blue-background": steelBlue.background,
	"--color-steel-blue-surface": steelBlue.surface,
	"--color-steel-blue-raised": steelBlue.raised,
	"--color-steel-blue-muted": steelBlue.muted,

	"--color-porcelain-background": porcelain.background,
	"--color-porcelain-surface": porcelain.surface,
	"--color-porcelain-muted": porcelain.muted,

	"--color-brand-icon-ivory": brand.iconIvory,
	"--color-brand-ink-text": brand.inkText,
	"--color-brand-secondary-text": brand.secondaryText,
	"--color-brand-muted-text": brand.mutedText,
	"--color-brand-navy": brand.navy,
	"--color-brand-navy-active": brand.navyActive,

	// NOTE (ZPL-101): the legacy semantic entries (--color-border,
	// --color-accent, --color-bg, …) were removed from this injection.
	// They were applied as INLINE styles on <html> and collided with
	// Tailwind v4's @theme tokens of the same names (--color-border,
	// --color-accent), silently overriding the runtime theme (steel-200
	// borders over the .dark palette). No web code consumed them; the
	// `semantic` TS export above remains for non-web consumers.
}
